import { useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiUser, FiBarChart2, FiTarget, FiUsers, FiActivity, FiTrendingUp, FiCheckCircle } from "react-icons/fi";
import "./PmsLogin.css";
import { requestPasswordReset, isResetEndpointMissing } from "../../services/authApi";

const USERNAME_PATTERN = /^[A-Za-z0-9_.@-]{3,100}$/;
// Matches PASSWORD_RESET_WINDOW_SECONDS=900 in the backend .env.
// Used only as a fallback when the server does not send a Retry-After header.
const RESET_WINDOW_MINUTES = 15;

function BrandMark() {
  return (
    <svg width="38" height="42" viewBox="0 0 38 42" aria-hidden="true" className="pmsx-logo-svg">
      <polygon points="19,1 36,10.5 36,31.5 19,41 2,31.5 2,10.5" fill="#ffffff" fillOpacity="0.14" stroke="#ffffff" strokeWidth="1.8" />
      <rect x="10" y="22" width="4" height="9" rx="1" fill="#ffffff" />
      <rect x="17" y="16" width="4" height="15" rx="1" fill="#ffffff" />
      <rect x="24" y="10" width="4" height="21" rx="1" fill="#4cc3ff" />
    </svg>
  );
}

function AuthNotice({ tone = "info", children }) {
  const icon = tone === "success" ? "✓" : tone === "error" ? "!" : "i";
  return (
    <div className={`pmsx-notice pmsx-notice--${tone}`} role={tone === "error" ? "alert" : "status"}>
      <span className="pmsx-notice-icon" aria-hidden="true">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function AuthShell({ title, subtitle, label, children }) {
  return (
    <main className="pmsx-root" aria-label={label}>
      <div className="pmsx-decor" aria-hidden="true">
        <span className="pmsx-hex pmsx-hex-1"><FiBarChart2 size={22} /></span>
        <span className="pmsx-hex pmsx-hex-2"><FiTarget size={20} /></span>
        <span className="pmsx-hex pmsx-hex-3"><FiUsers size={20} /></span>
        <span className="pmsx-hex pmsx-hex-4"><FiActivity size={22} /></span>
        <span className="pmsx-hex pmsx-hex-5"><FiTrendingUp size={20} /></span>
        <span className="pmsx-hex pmsx-hex-6"><FiCheckCircle size={18} /></span>
      </div>

      <section className="pmsx-panel">
        <div className="pmsx-brand">
          <BrandMark />
          <span className="pmsx-brand-name">TELIOLABS</span>
        </div>

        <div className="pmsx-heading">
          <h1 className="pmsx-title">{title}</h1>
          {subtitle ? <p className="pmsx-subtitle">{subtitle}</p> : null}
        </div>

        {children}
      </section>

      <footer className="pmsx-footer">
        <p>Not able to login? Contact HR Team</p>
        <a href="mailto:HR@teliolabs.com">HR@teliolabs.com</a>
        <p className="pmsx-copyright">&copy; 2026 Teliolabs. All rights reserved.</p>
      </footer>
    </main>
  );
}

export default function ForgotPasswordPage() {
  const [username, setUsername] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    const value = username.trim();
    if (!value) { setError("Enter your username."); return; }
    if (!USERNAME_PATTERN.test(value)) {
      setError("Usernames are 3-100 characters: letters, digits and . _ @ -");
      return;
    }
    setError("");
    setStatus("sending");
    try {
      await requestPasswordReset({ username: value });
      setStatus("sent");
    } catch (err) {
      if (isResetEndpointMissing(err)) { setStatus("unavailable"); return; }
      if (err?.response?.status === 429) {
        const secs = Number(err.response.headers?.["retry-after"]);
        const mins = secs > 0 ? Math.ceil(secs / 60) : RESET_WINDOW_MINUTES;
        setStatus("error");
        setError(
          secs > 0
            ? `Too many reset requests. Please try again in ${mins} minute${mins > 1 ? "s" : ""}.`
            : `Too many reset requests. Please try again in up to ${RESET_WINDOW_MINUTES} minutes.`
        );
        return;
      }
      if (!err?.response) {
        setStatus("error");
        setError("Unable to reach the server. Check your connection and try again.");
        return;
      }
      setStatus("sent");
    }
  };

  const busy = status === "sending";

  return (
    <AuthShell
      title="Forgot your password?"
      subtitle="Enter your username and we'll e-mail you a link to set a new password."
      label="Teliolabs PMS forgot password"
    >
      {status === "sent" ? (
        <div className="pmsx-form">
          <AuthNotice tone="success">
            If an account exists for <strong>{username.trim()}</strong>, a password reset link has been sent to its
            registered e-mail address. The link can be used once and expires soon.
          </AuthNotice>
          <p className="pmsx-hint">Didn't get it? Check your spam folder, or request a new link after 1 minute.</p>
          <Link to="/login" className="pmsx-login-btn pmsx-link-btn">Back to login</Link>
          <button type="button" className="pmsx-back-link" onClick={() => setStatus("idle")}>
            Send the link again
          </button>
        </div>
      ) : status === "unavailable" ? (
        <div className="pmsx-form">
          <AuthNotice tone="info">
            Password reset by e-mail isn't switched on yet. Please ask your administrator to issue a temporary
            password (Users &amp; roles). You'll be asked to choose a new password the next time you log in.
          </AuthNotice>
          <Link to="/login" className="pmsx-login-btn pmsx-link-btn">Back to login</Link>
        </div>
      ) : (
        <form className="pmsx-form" onSubmit={submit} noValidate>
          {error ? <AuthNotice tone="error">{error}</AuthNotice> : null}

          <div className="pmsx-field">
            <label className="pmsx-sr-only" htmlFor="pmsx-forgot-username">Username</label>
            <FiUser className="pmsx-field-icon" size={17} aria-hidden="true" />
            <input
              id="pmsx-forgot-username"
              type="text"
              autoComplete="username"
              placeholder="Email"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              disabled={busy}
              autoFocus
            />
          </div>

          <button type="submit" className="pmsx-login-btn" disabled={busy}>
            {busy ? (
              <span className="pmsx-btn-loading">
                <span className="pmsx-spinner" />
                <span>Sending link...</span>
              </span>
            ) : (
              "Send reset link"
            )}
          </button>

          <Link to="/login" className="pmsx-back-link">
            <FiArrowLeft size={15} aria-hidden="true" />
            <span>Back to login</span>
          </Link>
        </form>
      )}
    </AuthShell>
  );
}